import { useTranslation } from 'react-i18next';

import { ADMIN_PAGE_SIZES, buildPaginationItems } from '@/lib/pagination';

export type AdminPaginationProps = {
  /** 0-based current page. */
  page: number;
  pageSize: number;
  /** Exact total for the active filters (PostgREST `count: 'exact'`). */
  total: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  /**
   * Namespaces the rows-per-page control id when several paginated tables
   * share one page (email queue + logs).
   */
  idPrefix?: string;
};

/**
 * Pagination bar for admin tables — UI_Behavior §4.10 L661-667: summary
 * `{start}-{end} of {total}`, Prev/Next + page numbers (ellipsis beyond 7),
 * rows-per-page 20/50/100. The summary and size selector stay visible even
 * on a single page (so the empty state reads "0 of 0" and the size remains
 * adjustable); only the nav buttons collapse. Real `disabled` at the
 * bounds — aria-disabled alone blocks neither click nor keyboard.
 */
export function AdminPagination({
  page,
  pageSize,
  total,
  onPageChange,
  onPageSizeChange,
  idPrefix = 'admin-table',
}: AdminPaginationProps) {
  const { t } = useTranslation();

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  // Local clamp: an overshooting deep link (?page=99) renders ONE frame
  // before useAdminTablePagination's post-result clamp rewrites the URL —
  // the bar must never paint an incoherent "4901-30 of 30" in that frame.
  const safePage = Math.min(Math.max(0, page), totalPages - 1);
  const start = total === 0 ? 0 : safePage * pageSize + 1;
  const end = Math.min(total, (safePage + 1) * pageSize);
  const items = buildPaginationItems(safePage, totalPages);
  const sizeSelectId = `${idPrefix}-page-size`;

  return (
    <div className="admin-pagination">
      <span className="admin-pagination__summary">
        {total === 0
          ? t('admin.pagination.summaryEmpty')
          : t('admin.pagination.summary', { start, end, total })}
      </span>

      {totalPages > 1 ? (
        <nav className="admin-pagination__nav" aria-label={t('admin.pagination.ariaLabel')}>
          <button
            type="button"
            className="admin-pagination__btn admin-pagination__btn--nav"
            disabled={safePage <= 0}
            onClick={() => onPageChange(safePage - 1)}
          >
            {t('admin.pagination.prev')}
          </button>
          {items.map((item, index) =>
            item === 'ellipsis' ? (
              <span
                // Position-keyed: up to two ellipses, never reordered.
                key={`ellipsis-${index}`}
                className="admin-pagination__ellipsis"
                aria-hidden="true"
              >
                …
              </span>
            ) : (
              <button
                key={item}
                type="button"
                className={`admin-pagination__btn${item === safePage ? ' is-active' : ''}`}
                aria-current={item === safePage ? 'page' : undefined}
                aria-label={t('admin.pagination.goToPage', { page: item + 1 })}
                // Active page is inert — a no-op click must not reach the
                // URL layer (it would push a duplicate history entry).
                onClick={item === safePage ? undefined : () => onPageChange(item)}
              >
                {item + 1}
              </button>
            ),
          )}
          <button
            type="button"
            className="admin-pagination__btn admin-pagination__btn--nav"
            disabled={safePage >= totalPages - 1}
            onClick={() => onPageChange(safePage + 1)}
          >
            {t('admin.pagination.next')}
          </button>
        </nav>
      ) : null}

      <label className="admin-pagination__size" htmlFor={sizeSelectId}>
        <span>{t('admin.pagination.rowsPerPage')}</span>
        <select
          id={sizeSelectId}
          value={pageSize}
          onChange={(event) => onPageSizeChange(Number(event.target.value))}
        >
          {ADMIN_PAGE_SIZES.map((size) => (
            <option key={size} value={size}>
              {size}
            </option>
          ))}
        </select>
      </label>
    </div>
  );
}
